/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../PageLayout/LoginLayout';
import { useNavigate } from "react-router-dom";
import { loginWithCredentials, loginWithGoogleCredential } from "../services/auth.service";
import { formatApiError } from "../utils/apiError";
import { GoogleLogin } from "@react-oauth/google";

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaFor, setMfaFor] = useState(null); // 'password' | 'google' | null
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState('');
  const [showPassword, setShowPassword] = useState(false);
const [showEye, setShowEye] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const loginBtnRef = useRef(null);
  const [googleWidth, setGoogleWidth] = useState(0);

    const navigate = useNavigate();
  // const navigate = useNavigate();

  useEffect(() => {
    // Start with MFA hidden; we will show it only after discovering
    // the tenant and confirming it's not 'default' during login attempt.
    setNeedsMfa(false);
    setMfaFor(null);
    setPendingGoogleCredential("");
  }, []);

  // We only prompt for MFA after attempting login, not proactively while typing.

  // Using @react-oauth/google's GoogleLogin for account chooser + ID token callback
  useEffect(() => {
    const measure = () => {
      try {
        const w = loginBtnRef.current?.offsetWidth || 0;
        if (w && Math.abs(w - googleWidth) > 2) setGoogleWidth(w);
      } catch {}
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [googleWidth]);

  const inMfaStep = needsMfa === true;

  const resetMfaStep = () => {
    setNeedsMfa(false);
    setMfaFor(null);
    setMfaCode("");
    setPendingGoogleCredential("");
    setError("");
  };
  const handleLogin = async (e) => {
  if (e && typeof e.preventDefault === "function") e.preventDefault();
  if (needsMfa && mfaFor === "google") {
    await handleGoogleMfaVerify();
    return;
  }
  // Email validation
  if (!email) {
    alert('Please enter your email');
    return;
  }
  
  const emailRegex = /^[\w.%+-]+@(gmail\.com|outlook\.com|[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/i;
  if (!emailRegex.test(email)) {
    alert('Please enter a valid Gmail, Outlook, or company email');
    return;
  }

  // Password validation
  if (!password) {
    alert('Please enter your password');
    return;
  }
  if (needsMfa) {
    const code = String(mfaCode || "").trim();
    if (!/^(\d{6}|\d{8})$/.test(code)) {
      alert("Enter a valid 6- or 8-digit MFA code");
      return;
    }
  }

  // Authenticate via backend
  try {
    setSubmitting(true);
    setError('');
    try { localStorage.removeItem("authToken"); } catch {}

    // Attempt login first (discover-tenant happens inside the service)
    await loginWithCredentials(email, password, needsMfa ? mfaCode : undefined);

    // Default: proceed to overview
    navigate("/overview");
  } catch (e) {
    const status = e?.response?.status;
    const backendMsg = e?.response?.data?.message || e?.message || "" || "";
    const explicitMfa =
      e?.code === "MFA_REQUIRED" ||
      e?.response?.data?.code === "MFA_REQUIRED" ||
      e?.response?.data?.mfa_required === true ||
      (e?.response?.headers && String(e.response.headers['x-mfa-required']).toLowerCase() === "true") ||
      ((status === 400 || status === 401 || status === 403) && /mfa/i.test(backendMsg) && /(required|verify|code)/i.test(backendMsg));
    if (explicitMfa) {
      // Prompt for MFA code
      setNeedsMfa(true);
      setMfaFor("password");
      setError("Enter your 6‑ or 8‑digit MFA code to continue.");
    } else
    if (status === 401 || /invalid/i.test(backendMsg)) {
      alert("Email or password is invalid");
      setError("Email or password is invalid");
    } else {
      setError(backendMsg || "Login failed");
    }
  } finally {
    setSubmitting(false);
  }
};

  const handleGoogleMfaVerify = async () => {
    const cred = String(pendingGoogleCredential || "").trim();
    if (!cred) return;
    const code = String(mfaCode || "").trim();
    if (!/^(\d{6}|\d{8})$/.test(code)) {
      alert("Enter a valid 6- or 8-digit MFA code");
      return;
    }
    try {
      setSubmitting(true);
      setError("");
      await loginWithGoogleCredential(cred, code);
      navigate("/overview");
    } catch (err) {
      const msg = formatApiError(err) || err?.response?.data?.message || err?.message || "MFA verification failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <Layout>
      <h2 className="text-2xl font-semibold mb-1">Welcome</h2>
      <p className="text-sm text-gray-500 mb-6">
        Don’t have an account?{' '}
        <Link to="/signup" className="text-indigo-600 font-medium hover:underline">
          Sign up now
        </Link>
      </p>

      <form onSubmit={handleLogin}>
        {!inMfaStep && (
          <>
            {/* Email Input */}
            <div className="mb-4">
              <input
                type="email"
                placeholder="name.surname@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Password Input */}
            <div className="mb-2">
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setShowEye(e.target.value.length > 0);
                  }}
                  className="w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />

                {showEye && (
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-2 my-auto h-8 w-8 flex items-center justify-center rounded cursor-pointer border-0 bg-transparent p-0 pointer-events-auto z-10 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.694 12c2.25 3.75 5.94 6 9.806 6 1.514 0 2.96-.293 4.275-.828" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.228 6.228A10.478 10.478 0 0111.5 4.5c3.866 0 7.556 2.25 9.806 6a10.478 10.478 0 01-1.412 1.913" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1 1 0 010-.644C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.577 3.01 9.964 7.178.07.207.07.429 0 .636C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.577-3.01-9.964-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-sm text-right mb-6">
              <Link to="/forgot-password" className="text-gray-500 hover:underline">
                Forgot Password?
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={submitting}
              ref={loginBtnRef}
              className="w-full bg-[#1E1E2F] text-white rounded-md py-2 text-sm font-medium hover:bg-gray-800 mb-2 disabled:opacity-60"
            >
              {submitting ? "Signing in..." : "Login"}
            </button>
          </>
        )}

        {inMfaStep && (
          <>
            <div className="mb-4">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter 6‑ or 8‑digit MFA code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D+/g, "").slice(0, 8))}
                className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {mfaFor === "google" ? (
              <button
                type="button"
                disabled={submitting || loadingGoogle || !pendingGoogleCredential}
                onClick={handleGoogleMfaVerify}
                className="w-full bg-[#1E1E2F] text-white rounded-md py-2 text-sm font-medium hover:bg-gray-800 mb-2 disabled:opacity-60"
              >
                {submitting ? "Verifying..." : "Verify MFA & Continue"}
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1E1E2F] text-white rounded-md py-2 text-sm font-medium hover:bg-gray-800 mb-2 disabled:opacity-60"
              >
                {submitting ? "Verifying..." : "Verify MFA & Login"}
              </button>
            )}

            <button
              type="button"
              onClick={resetMfaStep}
              className="w-full text-sm text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </>
        )}
      </form>
      {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

      {!inMfaStep && (
        <>
          {/* Divider */}
          <div className="flex items-center justify-center mb-4">
            <hr className="w-full border-gray-200" />
            <span className="mx-3 text-xs text-gray-400">or</span>
            <hr className="w-full border-gray-200" />
          </div>

          {/* Google Login - sends credential (ID token) to backend */}
          <div className="w-full flex justify-center mt-2">
            <GoogleLogin
              width={googleWidth ? String(googleWidth) : undefined}
              logo_alignment="center"
              onSuccess={async (credentialResponse) => {
                try {
                  setLoadingGoogle(true);
                  setError("");
                  const cred = credentialResponse?.credential;
                  if (!cred) throw new Error("No Google credential received");
                  setPendingGoogleCredential(cred);
                  try {
                    await loginWithGoogleCredential(cred);
                    navigate("/overview");
                  } catch (err) {
                    const status = err?.response?.status;
                    const backendMsg = err?.response?.data?.message || err?.message || "";
                    const explicitMfa =
                      err?.code === "MFA_REQUIRED" ||
                      err?.response?.data?.code === "MFA_REQUIRED" ||
                      err?.response?.data?.mfa_required === true ||
                      (err?.response?.headers && String(err.response.headers['x-mfa-required']).toLowerCase() === "true") ||
                      ((status === 400 || status === 401 || status === 403) && /mfa/i.test(backendMsg) && /(required|verify|code)/i.test(backendMsg));
                    if (explicitMfa) {
                      setNeedsMfa(true);
                      setMfaFor("google");
                      setError("MFA is enabled for this Google account. Enter your code to continue.");
                    } else {
                      throw err;
                    }
                  }
                } catch (err) {
                  setError(formatApiError(err) || "Google login failed");
                } finally {
                  setLoadingGoogle(false);
                }
              }}
              onError={() => setError("Google sign-in was cancelled or failed")}
              useOneTap={false}
              shape="pill"
              theme="outline"
              size="large"
              text="continue_with"
            />
          </div>
        </>
      )}
    </Layout>
  );
}
